import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Pill, LifeBuoy, AlertTriangle, ChevronRight, Activity, Lock } from "lucide-react";
import api from "../../lib/api";

/**
 * HealthVaultStatus — non-sensitive widget for the /app shell.
 * Shows: meds active + "due today" chip list, emergency card status,
 * critical allergy count. NEVER shows values (BP/HR/glucose/etc.).
 * Only renders when the user is signed in AND has vault content.
 * Falls back to a "Start your Health Vault" CTA when empty.
 */

function fmtTimes(times) {
  if (!times || !times.length) return "";
  return times.join(" · ");
}

export default function HealthVaultStatus() {
  const [summary, setSummary] = useState(null);      // null=loading, false=signed-out/no-vault
  useEffect(() => {
    let cancelled = false;
    api.get("/vault/health/summary")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch(() => { if (!cancelled) setSummary(false); });
    return () => { cancelled = true; };
  }, []);

  // Not signed in → hide widget entirely; MSP already prompts sign-in elsewhere
  if (summary === false) return null;
  if (summary === null) {
    return (
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-6" data-testid="health-vault-status-loading">
        <div className="rounded-3xl bg-white border border-slate-200 p-5 animate-pulse h-24" />
      </div>
    );
  }

  const empty = summary.medications_active === 0 &&
                !summary.emergency_card_configured &&
                summary.vitals_last_7d === 0 &&
                summary.critical_allergies === 0;

  if (empty) {
    return (
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-6" data-testid="health-vault-status-empty">
        <Link to="/vault" data-testid="health-vault-empty-cta"
          className="block rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 hover:border-emerald-400 transition-colors p-5 group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white grid place-items-center border border-emerald-100">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-700">Safe Vault · Private</div>
              <div className="font-serif font-800 text-lg text-slate-900 mt-0.5">Start your Health Vault</div>
              <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1"><Lock className="w-3 h-3" /> PIN-locked. Never used for ads or analytics.</div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600" />
          </div>
        </Link>
      </section>
    );
  }

  const due = summary.medications_due_today || [];

  return (
    <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-6" data-testid="health-vault-status">
      <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-700">Safe Vault · at a glance</div>
              <div className="font-serif font-800 text-lg text-slate-900 leading-tight">My Health Vault</div>
            </div>
          </div>
          <Link to="/vault" data-testid="health-vault-status-open"
            className="inline-flex items-center gap-1 text-xs font-700 text-emerald-700 hover:underline">
            Open vault <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link to="/vault" data-testid="hvs-meds-tile"
            className="rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 p-3 transition-colors">
            <div className="flex items-center gap-1.5 text-[10px] font-800 uppercase tracking-wider text-emerald-700"><Pill className="w-3.5 h-3.5" /> Meds today</div>
            <div className="mt-1 text-2xl font-serif font-800 text-slate-900">{due.length}<span className="text-xs font-500 text-slate-500 ml-1">of {summary.medications_active}</span></div>
            {due.length > 0 ? (
              <div className="text-[11px] text-slate-600 mt-1 line-clamp-2" data-testid="hvs-due-names">
                {due.slice(0, 3).map((m) => m.name).join(", ")}{due.length > 3 ? "…" : ""}
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 mt-1">No scheduled doses</div>
            )}
          </Link>

          <Link to="/vault" data-testid="hvs-emergency-tile"
            className={`rounded-2xl bg-white border p-3 transition-colors ${summary.emergency_card_configured ? "border-slate-200 hover:border-rose-400" : "border-amber-300 hover:border-amber-500"}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-800 uppercase tracking-wider text-rose-700"><LifeBuoy className="w-3.5 h-3.5" /> Emergency card</div>
            <div className="mt-1 text-2xl font-serif font-800 text-slate-900">
              {summary.emergency_card_configured ? "Ready" : "Setup"}
            </div>
            <div className="text-[11px] text-slate-600 mt-1">
              {summary.emergency_card_configured
                ? `${summary.emergency_card_field_count} field${summary.emergency_card_field_count === 1 ? "" : "s"} · ${summary.active_shares} active share${summary.active_shares === 1 ? "" : "s"}`
                : "Pick fields to share in an emergency"}
            </div>
          </Link>

          <Link to="/vault" data-testid="hvs-allergies-tile"
            className="rounded-2xl bg-white border border-slate-200 hover:border-rose-400 p-3 transition-colors">
            <div className="flex items-center gap-1.5 text-[10px] font-800 uppercase tracking-wider text-rose-700"><AlertTriangle className="w-3.5 h-3.5" /> Critical allergies</div>
            <div className="mt-1 text-2xl font-serif font-800 text-slate-900">{summary.critical_allergies}</div>
            <div className="text-[11px] text-slate-500 mt-1">Flagged for emergency responders</div>
          </Link>

          <Link to="/vault" data-testid="hvs-vitals-tile"
            className="rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 p-3 transition-colors">
            <div className="flex items-center gap-1.5 text-[10px] font-800 uppercase tracking-wider text-emerald-700"><Activity className="w-3.5 h-3.5" /> Vitals · 7d</div>
            <div className="mt-1 text-2xl font-serif font-800 text-slate-900">{summary.vitals_last_7d}</div>
            <div className="text-[11px] text-slate-500 mt-1">Readings this week</div>
          </Link>
        </div>

        {due.length > 0 && (
          <div className="mt-4 border-t border-emerald-100 pt-3" data-testid="hvs-due-detail">
            <div className="text-[10px] font-800 uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1"><Pill className="w-3 h-3" /> Scheduled today</div>
            <div className="flex flex-wrap gap-2">
              {due.map((m) => (
                <Link key={m.id} to="/vault" data-testid={`hvs-due-${m.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-200 hover:border-emerald-500 px-2.5 py-1 text-xs">
                  <span className="font-700 text-slate-800">{m.name}</span>
                  {m.times && m.times.length > 0 && <span className="font-mono text-[10px] text-slate-500">{fmtTimes(m.times)}</span>}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 text-[10px] text-slate-500 flex items-center gap-1">
          <Lock className="w-3 h-3" /> No values shown here. Open the vault to see actual readings — private, PIN-locked, never used for ads.
        </div>
      </div>
    </section>
  );
}
